import React, { createContext, useCallback, useContext, useMemo, useRef, ReactNode } from 'react';
import { Wrapper } from './ScrollableTableWrapper.styles';
import { useHorizontalScrollIndicators } from '../../hooks/useHorizontalScrollIndicators';

interface ScrollableTableContextValue {
	showLeft: boolean;
	showRight: boolean;
	scrollLeft: () => void;
	scrollRight: () => void;
}

const ScrollableTableContext = createContext<ScrollableTableContextValue>({
	showLeft: false,
	showRight: false,
	scrollLeft: () => undefined,
	scrollRight: () => undefined,
});

export const useScrollableTable = (): ScrollableTableContextValue => useContext(ScrollableTableContext);

interface ScrollableTableWrapperProps {
	children: ReactNode;
}

export const ScrollableTableWrapper: React.FC<ScrollableTableWrapperProps> = ({ children }) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const { showLeftShadow, showRightShadow } = useHorizontalScrollIndicators(scrollRef);

	const scrollBy = useCallback((direction: 'left' | 'right') => {
		const element = scrollRef.current;
		if (!element) return;
		const amount = element.clientWidth * 0.8;
		element.scrollTo({
			left: direction === 'left' ? element.scrollLeft - amount : element.scrollLeft + amount,
			behavior: 'smooth',
		});
	}, []);

	const value = useMemo<ScrollableTableContextValue>(
		() => ({
			showLeft: showLeftShadow,
			showRight: showRightShadow,
			scrollLeft: () => scrollBy('left'),
			scrollRight: () => scrollBy('right'),
		}),
		[showLeftShadow, showRightShadow, scrollBy]
	);

	return (
		<ScrollableTableContext.Provider value={value}>
			<Wrapper $showLeft={showLeftShadow} $showRight={showRightShadow}>
				{React.Children.map(children, (child) => {
					if (React.isValidElement(child)) {
						return React.cloneElement(child as React.ReactElement<any>, { ref: scrollRef });
					}
					return child;
				})}
			</Wrapper>
		</ScrollableTableContext.Provider>
	);
};
